import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import api from "../../utils/api";
import { clearCart } from "../../utils/cartUtils";
import { toLocalizedPrice } from "../../utils/localeUtils";
import { FaCheck, FaTimes } from "react-icons/fa";
import "./PaymentCallback.css";

const PaymentCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState(t("paymentCallback.verifying"));
  const [paying, setPaying] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(null);
  const verifyAttempted = useRef(false);

  const [orderId, setOrderId] = useState(
    searchParams.get("orderId") || searchParams.get("purchase_order_id"),
  );
  const pidx = searchParams.get("pidx");
  const transactionId =
    searchParams.get("transaction_id") || searchParams.get("txnId");
  const paymentStatus = searchParams.get("status");
  const gateway = searchParams.get("gateway");

  // Extract eSewa 'data' param from raw URL to avoid URLSearchParams converting + to space,
  // which corrupts the Base64-encoded response from eSewa.
  const esewaData = (() => {
    const match = window.location.search.match(/[?&]data=([^&]*)/);
    return match ? decodeURIComponent(match[1]) : null;
  })();

  const isEsewa = gateway === "esewa" || !!esewaData;

  const handlePayAgain = async () => {
    if (!orderId) return;
    setPaying(true);
    try {
      if (isEsewa) {
        const res = await api.post("/payments/esewa/initiate", { orderId });
        const { esewaFormData } = res.data;
        if (esewaFormData) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = esewaFormData.url;
          const { url, ...fields } = esewaFormData;
          Object.entries(fields).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
        throw new Error("No eSewa form data");
      } else {
        const res = await api.post("/payments/khalti/initiate", { orderId });
        if (res.data?.payment_url) {
          window.location.href = res.data.payment_url;
          return;
        }
        throw new Error("No payment URL");
      }
    } catch (err) {
      setPaying(false);
      console.error("Pay again error:", err);
    }
  };

  useEffect(() => {
    if (verifyAttempted.current) return;
    verifyAttempted.current = true;

    const verifyPayment = async () => {
      // eSewa success callback: `data` query param contains Base64-encoded response
      if (esewaData) {
        try {
          const response = await api.post("/payments/esewa/verify", {
            data: esewaData,
          });

          const verifiedOrderId =
            response.data.order?._id ||
            response.data.payment?.order?._id ||
            response.data.payment?.order;
          if (verifiedOrderId) setOrderId(verifiedOrderId);

          if (response.data.payment.paymentStatus === "completed") {
            setStatus("success");
            setMessage(t("paymentCallback.paymentSuccess"));
            const amount =
              response.data.payment?.amount ?? response.data.order?.totalPrice;
            setPaymentAmount(amount != null ? amount : null);
            clearCart();
            setTimeout(() => {
              navigate(`/order/${verifiedOrderId || orderId}`);
            }, 2000);
          } else {
            setStatus("error");
            setMessage(t("paymentCallback.verificationFailed"));
          }
        } catch (error) {
          console.error("eSewa verification error:", error);
          setStatus("error");
          setMessage(
            error.response?.data?.message ||
              t("paymentCallback.verificationFailed"),
          );
        }
        return;
      }

      // eSewa failure redirect (no data param — eSewa sends to failure_url without data on cancel/failure)
      if (isEsewa && !esewaData) {
        setStatus("error");
        setMessage(t("paymentCallback.paymentCanceled"));
        return;
      }

      // eSewa redirected without any identifiable params — likely a bare failure redirect
      if (!orderId && !pidx && !esewaData && !gateway) {
        setStatus("error");
        setMessage(t("paymentCallback.paymentCanceled"));
        return;
      }

      // Khalti callback — needs orderId and pidx
      if (!orderId || !pidx) {
        setStatus("error");
        setMessage(t("paymentCallback.invalidCallback"));
        return;
      }

      if (
        paymentStatus &&
        paymentStatus !== "Completed" &&
        paymentStatus !== "Pending"
      ) {
        if (paymentStatus === "User canceled") {
          setStatus("error");
          setMessage(t("paymentCallback.paymentCanceled"));
        } else {
          setStatus("error");
          setMessage(t("paymentCallback.verificationFailed"));
        }
        return;
      }

      try {
        const response = await api.post(
          "/payments/khalti/verify",
          {
            pidx,
            transaction_id: transactionId,
            status: paymentStatus,
          },
          {
            params: { orderId },
          },
        );

        if (response.data.payment.paymentStatus === "completed") {
          setStatus("success");
          setMessage(t("paymentCallback.paymentSuccess"));
          const amount =
            response.data.payment?.amount ?? response.data.order?.totalPrice;
          setPaymentAmount(amount != null ? amount : null);
          clearCart();
          setTimeout(() => {
            navigate(`/order/${orderId}`);
          }, 2000);
        } else {
          setStatus("error");
          setMessage(t("paymentCallback.verificationFailed"));
        }
      } catch (error) {
        console.error("Khalti verification error:", error);
        setStatus("error");
        setMessage(
          error.response?.data?.message ||
            t("paymentCallback.verificationFailed"),
        );
      }
    };

    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="payment-callback-page">
      <Header />
      <div className="payment-callback-container">
        <div className={`payment-callback-status ${status}`}>
          {status === "processing" && (
            <div className="payment-callback-spinner-container">
              <div className="payment-callback-spinner"></div>
            </div>
          )}
          {status === "success" && (
            <div className="payment-callback-success-icon" aria-hidden>
              <FaCheck />
            </div>
          )}
          {status === "error" && (
            <div className="payment-callback-error-icon" aria-hidden>
              <FaTimes />
            </div>
          )}
          <h2>{message}</h2>
          {paymentAmount != null && (
            <p className="payment-callback-amount">
              Rs. {toLocalizedPrice(paymentAmount)}
            </p>
          )}
          {orderId && (
            <p className="payment-callback-order-id">
              {t("paymentCallback.orderId", { orderId })}
            </p>
          )}
          {status === "error" && (
            <div className="payment-callback-error-actions">
              {orderId && (
                <>
                  <button
                    onClick={handlePayAgain}
                    disabled={paying}
                    className={`payment-callback-btn-pay-again ${isEsewa ? "payment-callback-btn-esewa" : "payment-callback-btn-khalti"}`}
                  >
                    {paying
                      ? t("paymentCallback.redirecting")
                      : t("paymentCallback.payAgain")}
                  </button>
                  <button
                    onClick={() => navigate(`/order/${orderId}`)}
                    className="payment-callback-btn-view-order"
                  >
                    {t("paymentCallback.viewOrder")}
                  </button>
                </>
              )}
              <button
                onClick={() => navigate(orderId ? "/cart" : "/orders")}
                className="payment-callback-btn-back-cart"
              >
                {orderId
                  ? t("paymentCallback.backToCart")
                  : t("paymentCallback.viewOrders", "View My Orders")}
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentCallback;
